// /teacher/conferences — manage parent-teacher conference availability.
//
// Lets a teacher publish open slots either via a bulk template
// (date-range × time-of-day × duration) or one-by-one. Lists upcoming
// slots with their bookings and supports cancellation and post-meeting
// status (completed / no_show).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarPlus,
  CalendarBlank,
  Plus,
  Trash,
  CheckCircle,
  XCircle,
  Clock,
  Sparkle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listTeacherClassrooms } from "@/lib/dailyReports";
import {
  cancelBooking,
  cancelSlot,
  createSlot,
  createSlotsBulk,
  deleteSlot,
  expandTemplate,
  formatSlotRange,
  listMyTeacherSlots,
  teacherMarkBooking,
} from "@/lib/conferences";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const DAY_OPTIONS = [
  { v: 1, l: "Mon" },
  { v: 2, l: "Tue" },
  { v: 3, l: "Wed" },
  { v: 4, l: "Thu" },
  { v: 5, l: "Fri" },
  { v: 6, l: "Sat" },
  { v: 0, l: "Sun" },
];

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}
function plusDays(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function TeacherConferences() {
  const { profile } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [singleOpen, setSingleOpen] = useState(false);

  const refresh = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const [cls, mine] = await Promise.all([
        listTeacherClassrooms(profile.id),
        listMyTeacherSlots({ teacherProfileId: profile.id }),
      ]);
      setClassrooms(cls || []);
      setSlots(mine || []);
    } catch (err) {
      toast.error(err.message || "Could not load conferences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const grouped = useMemo(() => groupByDay(slots), [slots]);
  const upcomingCount = useMemo(
    () => slots.filter((s) => new Date(s.start_at) > new Date()).length,
    [slots],
  );
  const bookedCount = useMemo(
    () =>
      slots.reduce(
        (n, s) => n + (s.bookings?.filter((b) => b.status === "booked").length || 0),
        0,
      ),
    [slots],
  );

  const onCancelSlot = async (slot) => {
    const hasBooked = slot.bookings?.some((b) => b.status === "booked");
    const msg = hasBooked
      ? "This slot has a booking. Cancelling will keep the booking row but mark the slot as cancelled. Continue?"
      : "Cancel this open slot?";
    if (!window.confirm(msg)) return;
    try {
      await cancelSlot(slot.id);
      toast.success("Slot cancelled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  const onDeleteSlot = async (slot) => {
    if (slot.bookings?.length) {
      toast.error("This slot has bookings — cancel it instead.");
      return;
    }
    if (!window.confirm("Delete this open slot?")) return;
    try {
      await deleteSlot(slot.id);
      toast.success("Slot deleted");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not delete");
    }
  };

  const onMarkBooking = async (booking, status) => {
    try {
      await teacherMarkBooking({ id: booking.id, status });
      toast.success(status === "completed" ? "Marked completed" : "Marked no-show");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not update");
    }
  };

  const onCancelBooking = async (booking) => {
    if (!window.confirm("Cancel this booking on behalf of the family?")) return;
    try {
      await cancelBooking(booking.id);
      toast.success("Booking cancelled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  return (
    <div data-testid="teacher-conferences" className="space-y-5">
      <div>
        <Link
          to="/teacher"
          data-testid="teacher-conferences-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" />
          Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Conferences
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          Publish parent-teacher conference slots. Parents book directly from
          their app and you get a confirmation in your messages.
        </p>
      </div>

      <section className="grid grid-cols-2 gap-3">
        <StatCard label="Upcoming slots" value={String(upcomingCount)} />
        <StatCard label="Booked" value={String(bookedCount)} />
      </section>

      <section className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="teacher-conferences-bulk"
          onClick={() => setBulkOpen(true)}
          disabled={!classrooms.length}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
        >
          <CalendarPlus size={14} weight="duotone" />
          Bulk template
        </button>
        <button
          type="button"
          data-testid="teacher-conferences-single"
          onClick={() => setSingleOpen(true)}
          disabled={!classrooms.length}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-800 hover:bg-neutral-50 disabled:opacity-60"
        >
          <Plus size={14} weight="bold" />
          Add single slot
        </button>
      </section>

      {!classrooms.length && (
        <p
          data-testid="teacher-conferences-no-classroom"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500"
        >
          You're not assigned to a classroom yet. Once you are, you'll be able
          to publish conference slots here.
        </p>
      )}

      {loading && (
        <p className="text-sm text-neutral-500">Loading…</p>
      )}

      {!loading && classrooms.length > 0 && grouped.length === 0 && (
        <p
          data-testid="teacher-conferences-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500"
        >
          No upcoming slots yet — add one to get started.
        </p>
      )}

      <ul className="space-y-4" data-testid="teacher-conferences-list">
        {grouped.map(([day, daySlots]) => (
          <li key={day}>
            <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">
              {day}
            </p>
            <ul className="space-y-2">
              {daySlots.map((s) => (
                <SlotRow
                  key={s.id}
                  slot={s}
                  onCancelSlot={() => onCancelSlot(s)}
                  onDeleteSlot={() => onDeleteSlot(s)}
                  onMarkBooking={onMarkBooking}
                  onCancelBooking={onCancelBooking}
                />
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <BulkTemplateSheet
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        classrooms={classrooms}
        teacherProfileId={profile?.id}
        organizationId={profile?.organization_id}
        onSaved={() => refresh()}
      />
      <SingleSlotSheet
        open={singleOpen}
        onOpenChange={setSingleOpen}
        classrooms={classrooms}
        teacherProfileId={profile?.id}
        organizationId={profile?.organization_id}
        onSaved={() => refresh()}
      />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">
        {value}
      </p>
    </div>
  );
}

function groupByDay(slots) {
  const map = new Map();
  for (const s of slots) {
    const d = new Date(s.start_at);
    const label = d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
    if (!map.has(label)) map.set(label, []);
    map.get(label).push(s);
  }
  return Array.from(map.entries());
}

function SlotRow({ slot, onCancelSlot, onDeleteSlot, onMarkBooking, onCancelBooking }) {
  const past = new Date(slot.start_at) <= new Date();
  const booked = slot.bookings?.find((b) => b.status === "booked");
  const completed = slot.bookings?.find((b) => b.status === "completed");
  const noShow = slot.bookings?.find((b) => b.status === "no_show");

  let statusChip;
  if (slot.status === "cancelled") {
    statusChip = <Chip tone="error">Cancelled</Chip>;
  } else if (booked) {
    statusChip = <Chip tone="primary">Booked</Chip>;
  } else if (completed) {
    statusChip = <Chip tone="ok">Completed</Chip>;
  } else if (noShow) {
    statusChip = <Chip tone="warn">No-show</Chip>;
  } else {
    statusChip = <Chip tone="muted">Open</Chip>;
  }

  const activeOrPastBooking = booked || completed || noShow;

  return (
    <li
      data-testid={`teacher-conference-slot-${slot.id}`}
      data-status={slot.status}
      data-booking-status={activeOrPastBooking?.status || "none"}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <Clock size={16} weight="duotone" className="mt-0.5 text-neutral-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">
            {formatSlotRange(slot.start_at, slot.end_at)}
          </p>
          <p className="text-[11px] text-neutral-500">
            {slot.classroom?.name || "Classroom"}
            {slot.location ? ` · ${slot.location}` : ""}
            {slot.notes ? ` · ${slot.notes}` : ""}
          </p>
          {activeOrPastBooking && (
            <p className="mt-1 text-[12px] text-neutral-700">
              {activeOrPastBooking.child?.first_name}
              {activeOrPastBooking.child?.last_name
                ? ` ${activeOrPastBooking.child.last_name}`
                : ""}
            </p>
          )}
        </div>
        {statusChip}
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
        {/* Booked + future: family can cancel from their side too;
            teacher can also cancel-on-behalf or mark statuses post-meeting. */}
        {booked && past && (
          <>
            <button
              type="button"
              data-testid={`teacher-conference-mark-completed-${slot.id}`}
              onClick={() => onMarkBooking(booked, "completed")}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-50"
            >
              <CheckCircle size={11} weight="duotone" />
              Completed
            </button>
            <button
              type="button"
              data-testid={`teacher-conference-mark-no-show-${slot.id}`}
              onClick={() => onMarkBooking(booked, "no_show")}
              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-50"
            >
              <XCircle size={11} weight="duotone" />
              No-show
            </button>
          </>
        )}
        {booked && !past && (
          <button
            type="button"
            data-testid={`teacher-conference-cancel-booking-${slot.id}`}
            onClick={() => onCancelBooking(booked)}
            className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
          >
            Cancel booking
          </button>
        )}
        {slot.status === "open" && !booked && !past && (
          <>
            <button
              type="button"
              data-testid={`teacher-conference-slot-cancel-${slot.id}`}
              onClick={onCancelSlot}
              className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Cancel slot
            </button>
            <button
              type="button"
              data-testid={`teacher-conference-slot-delete-${slot.id}`}
              onClick={onDeleteSlot}
              className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
            >
              <Trash size={11} weight="duotone" />
              Delete
            </button>
          </>
        )}
        {slot.status === "cancelled" && !booked && (
          <button
            type="button"
            data-testid={`teacher-conference-slot-delete-${slot.id}`}
            onClick={onDeleteSlot}
            className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
          >
            <Trash size={11} weight="duotone" />
            Delete
          </button>
        )}
      </div>
    </li>
  );
}

function Chip({ tone, children }) {
  const toneClass = {
    primary: "bg-primary/10 text-primary-hover",
    ok: "bg-emerald-50 text-emerald-800",
    warn: "bg-amber-50 text-amber-800",
    error: "bg-[#C6851E]/10 text-[#8A5A12]",
    muted: "bg-neutral-100 text-neutral-700",
  }[tone || "muted"];
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${toneClass}`}
    >
      {children}
    </span>
  );
}

function BulkTemplateSheet({ open, onOpenChange, classrooms, teacherProfileId, organizationId, onSaved }) {
  const [classroomId, setClassroomId] = useState(classrooms[0]?.id || "");
  const [startDate, setStartDate] = useState(todayIsoDate());
  const [endDate, setEndDate] = useState(plusDays(todayIsoDate(), 6));
  const [dayStart, setDayStart] = useState("16:00");
  const [dayEnd, setDayEnd] = useState("18:00");
  const [slotMinutes, setSlotMinutes] = useState(15);
  const [days, setDays] = useState([1, 2, 3, 4, 5]);
  const [breakStart, setBreakStart] = useState("");
  const [breakEnd, setBreakEnd] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setClassroomId(classrooms[0]?.id || "");
    }
  }, [open, classrooms]);

  const classroom = classrooms.find((c) => c.id === classroomId);

  const preview = useMemo(() => {
    if (!classroomId || !classroom || !organizationId) return [];
    try {
      return expandTemplate({
        startDate,
        endDate,
        dayStart,
        dayEnd,
        slotMinutes: Number(slotMinutes) || 15,
        days,
        breaks: breakStart && breakEnd ? [{ start: breakStart, end: breakEnd }] : [],
        organizationId,
        teacherProfileId,
        classroomId,
        location: location.trim() || null,
      });
    } catch {
      return [];
    }
  }, [
    classroomId, classroom, organizationId, startDate, endDate, dayStart, dayEnd,
    slotMinutes, days, breakStart, breakEnd, teacherProfileId, location,
  ]);

  const onCreate = async () => {
    if (!preview.length) {
      toast.error("Template produced no slots — check your settings.");
      return;
    }
    setBusy(true);
    try {
      await createSlotsBulk(preview);
      toast.success(`${preview.length} slot${preview.length === 1 ? "" : "s"} published`);
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not create slots");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="teacher-conferences-bulk-sheet"
        side="bottom"
        className="rounded-t-3xl border-t border-neutral-200 sm:max-w-lg sm:mx-auto max-h-[90vh] overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">Bulk template</SheetTitle>
          <SheetDescription>
            Generate a batch of identical-length slots across a date range.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <Field label="Classroom">
            <select
              data-testid="teacher-conferences-bulk-classroom"
              value={classroomId}
              onChange={(e) => setClassroomId(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {classrooms.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <input
                type="date"
                data-testid="teacher-conferences-bulk-start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="End date">
              <input
                type="date"
                data-testid="teacher-conferences-bulk-end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="Day starts">
              <input
                type="time"
                data-testid="teacher-conferences-bulk-day-start"
                value={dayStart}
                onChange={(e) => setDayStart(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="Day ends">
              <input
                type="time"
                data-testid="teacher-conferences-bulk-day-end"
                value={dayEnd}
                onChange={(e) => setDayEnd(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="Slot length (min)">
              <input
                type="number"
                min="5"
                step="5"
                data-testid="teacher-conferences-bulk-minutes"
                value={slotMinutes}
                onChange={(e) => setSlotMinutes(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="Location (optional)">
              <input
                type="text"
                data-testid="teacher-conferences-bulk-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Room A · Zoom · …"
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
          </div>

          <Field label="Days of week">
            <div className="flex flex-wrap gap-1.5">
              {DAY_OPTIONS.map((d) => {
                const on = days.includes(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    data-testid={`teacher-conferences-bulk-day-${d.v}`}
                    aria-pressed={on}
                    onClick={() =>
                      setDays((prev) =>
                        prev.includes(d.v)
                          ? prev.filter((x) => x !== d.v)
                          : [...prev, d.v].sort(),
                      )
                    }
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                      on
                        ? "bg-primary text-white"
                        : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                    }`}
                  >
                    {d.l}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Break start (optional)">
              <input
                type="time"
                data-testid="teacher-conferences-bulk-break-start"
                value={breakStart}
                onChange={(e) => setBreakStart(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="Break end (optional)">
              <input
                type="time"
                data-testid="teacher-conferences-bulk-break-end"
                value={breakEnd}
                onChange={(e) => setBreakEnd(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
          </div>

          <p
            data-testid="teacher-conferences-bulk-preview"
            data-count={preview.length}
            className="rounded-xl bg-primary/5 px-3 py-2 text-sm text-primary-hover"
          >
            <Sparkle size={12} weight="duotone" className="mr-1 inline" />
            Preview: {preview.length} slot{preview.length === 1 ? "" : "s"} will be created.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="teacher-conferences-bulk-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="teacher-conferences-bulk-confirm"
            disabled={busy || preview.length === 0}
            onClick={onCreate}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            <CalendarPlus size={12} weight="duotone" />
            {busy ? "Publishing…" : `Publish ${preview.length}`}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SingleSlotSheet({ open, onOpenChange, classrooms, teacherProfileId, organizationId, onSaved }) {
  const [classroomId, setClassroomId] = useState(classrooms[0]?.id || "");
  const [date, setDate] = useState(todayIsoDate());
  const [startTime, setStartTime] = useState("16:00");
  const [endTime, setEndTime] = useState("16:15");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setClassroomId(classrooms[0]?.id || "");
  }, [open, classrooms]);

  const onCreate = async () => {
    if (!classroomId) {
      toast.error("Pick a classroom");
      return;
    }
    const startAt = new Date(`${date}T${startTime}:00`);
    const endAt = new Date(`${date}T${endTime}:00`);
    if (!(endAt > startAt)) {
      toast.error("End time must be after start.");
      return;
    }
    setBusy(true);
    try {
      await createSlot({
        organizationId,
        teacherProfileId,
        classroomId,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        location,
        notes,
      });
      toast.success("Slot created");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="teacher-conferences-single-sheet"
        side="bottom"
        className="rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">Add a slot</SheetTitle>
          <SheetDescription>
            Quick one-off — useful for make-up meetings.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <Field label="Classroom">
            <select
              data-testid="teacher-conferences-single-classroom"
              value={classroomId}
              onChange={(e) => setClassroomId(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {classrooms.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date">
            <input
              type="date"
              data-testid="teacher-conferences-single-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start">
              <input
                type="time"
                data-testid="teacher-conferences-single-start"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
            <Field label="End">
              <input
                type="time"
                data-testid="teacher-conferences-single-end"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </Field>
          </div>
          <Field label="Location (optional)">
            <input
              type="text"
              data-testid="teacher-conferences-single-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </Field>
          <Field label="Notes (optional)">
            <textarea
              rows={2}
              data-testid="teacher-conferences-single-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="teacher-conferences-single-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="teacher-conferences-single-confirm"
            disabled={busy}
            onClick={onCreate}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            <Plus size={12} weight="bold" />
            {busy ? "Saving…" : "Create slot"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}
